import { getLogger } from "@expert/logging";
import { expertWorkspaceWebSocketEventBus } from "@expert/websocket";
import { useEffect } from "react";

import { useChatStore } from "@/sdk/agent/chat";
import { userCache } from "@/shared-utils";

export interface AutopilotEventPayload {
    chatId: string;
    expertEmployeeId: string;
    durationSeconds?: number;
    startTime?: number;
    endTime?: number;
}

// Module-scoped (not the React context logger): this handles events for ANY of the expert's chats,
// so inheriting the active chat's identity would mis-attribute background events under concurrent
// chats. Identity is bound per-event from the chat the event is about instead.
const logger = getLogger({ module: "useAutopilotEventsListener", supportTeam: "messaging" });

export const useAutopilotEventsListener = () => {
    useEffect(() => {
        return expertWorkspaceWebSocketEventBus.on("ws_chat-autopilot-event", (payload: AutopilotEventPayload) => {
            const { chatId, expertEmployeeId, startTime, durationSeconds, endTime } = payload;

            // For Connect chat, chatId === sessionId === contactId, so the event's own chatId is the
            // correlation id — no session lookup needed.
            const eventLogger = logger.child({ chatId, sessionId: chatId });

            eventLogger.info({ autopilotEventPayload: JSON.stringify(payload) }, "Autopilot event received");

            if (expertEmployeeId !== userCache.employeeId) {
                eventLogger.debug(
                    { expertEmployeeId, userCacheEmployeeId: userCache.employeeId },
                    "Ignoring autopilot event for different expert",
                );
                return;
            }

            // If no endTime, autopilot is starting/active - set the state
            if (endTime) {
                useChatStore.getState().clearChatAutopilot(chatId);
            } else if (startTime) {
                useChatStore.getState().updateChatAutopilot(chatId, { startTime, durationSeconds });
            }
        });
    }, []);
};
